/**
 * NPCPlacements.ts - NPC position data for all locations
 * Maps NPC IDs to their locations and sprite assignments
 */

import { Location } from './StoryData';

export interface NPCPlacement {
  npcId: string;
  name: string;
  location: Location;
  x: number; // Tile X position
  y: number; // Tile Y position
  facing: 'up' | 'down' | 'left' | 'right';
  spriteClass?: string; // Character class sprite to use (FIGHTER, THIEF, MONK, etc.)
  customSprite?: string; // Or custom sprite ID for unique NPCs
  wanders?: boolean; // Does NPC move around?
  blockMovement?: boolean; // Does NPC block player movement?
}

// ============================================================================
// NPC PLACEMENTS BY LOCATION
// ============================================================================

export const NPC_PLACEMENTS: NPCPlacement[] = [
  // =========================================================================
  // CORNELIA - Starting Town
  // =========================================================================
  {
    npcId: 'CORNELIA_OLD_MAN',
    name: 'Old Man',
    location: Location.CORNELIA,
    x: 5,
    y: 8,
    facing: 'down',
    spriteClass: 'OLD_MAN',
    blockMovement: true,
  },
  {
    npcId: 'CORNELIA_WOMAN',
    name: 'Woman',
    location: Location.CORNELIA,
    x: 12,
    y: 10,
    facing: 'left',
    spriteClass: 'WOMAN',
    blockMovement: true,
  },
  {
    npcId: 'CORNELIA_GUARD',
    name: 'Guard',
    location: Location.CORNELIA,
    x: 8,
    y: 4,
    facing: 'down',
    spriteClass: 'FIGHTER',
    blockMovement: true,
  },
  {
    npcId: 'CORNELIA_SHOP_KEEPER',
    name: 'Shop Keeper',
    location: Location.CORNELIA,
    x: 15,
    y: 7,
    facing: 'down',
    spriteClass: 'MERCHANT',
    blockMovement: true,
  },
  {
    npcId: 'CORNELIA_INN_KEEPER',
    name: 'Inn Keeper',
    location: Location.CORNELIA,
    x: 3,
    y: 12,
    facing: 'down',
    spriteClass: 'MERCHANT',
    blockMovement: true,
  },
  {
    npcId: 'CORNELIA_SCHOLAR',
    name: 'Scholar',
    location: Location.CORNELIA,
    x: 10,
    y: 14,
    facing: 'up',
    spriteClass: 'RED_MAGE',
    blockMovement: true,
  },

  // =========================================================================
  // CORNELIA CASTLE
  // =========================================================================
  {
    npcId: 'KING',
    name: 'King of Cornelia',
    location: Location.CORNELIA_CASTLE,
    x: 8,
    y: 5,
    facing: 'down',
    spriteClass: 'KING',
    blockMovement: true,
  },
  {
    npcId: 'PRINCESS_SARA',
    name: 'Princess Sara',
    location: Location.CORNELIA_CASTLE,
    x: 10,
    y: 5,
    facing: 'down',
    spriteClass: 'PRINCESS',
    blockMovement: true,
  },
  {
    npcId: 'CASTLE_MINISTER',
    name: 'Minister',
    location: Location.CORNELIA_CASTLE,
    x: 6,
    y: 7,
    facing: 'right',
    spriteClass: 'OLD_MAN',
    blockMovement: true,
  },

  // =========================================================================
  // PRAVOKA - Port Town
  // =========================================================================
  {
    npcId: 'PRAVOKA_SAILOR',
    name: 'Sailor',
    location: Location.PRAVOKA,
    x: 14,
    y: 12,
    facing: 'left',
    spriteClass: 'THIEF',
    blockMovement: true,
  },
  {
    npcId: 'PRAVOKA_WOMAN',
    name: 'Woman',
    location: Location.PRAVOKA,
    x: 7,
    y: 8,
    facing: 'down',
    spriteClass: 'WOMAN',
    blockMovement: true,
  },
  {
    npcId: 'PRAVOKA_MERCHANT',
    name: 'Merchant',
    location: Location.PRAVOKA,
    x: 11,
    y: 6,
    facing: 'down',
    spriteClass: 'MERCHANT',
    blockMovement: true,
  },

  // =========================================================================
  // ELFHEIM - Elf Town
  // =========================================================================
  {
    npcId: 'ELFHEIM_ELF_1',
    name: 'Elf',
    location: Location.ELFHEIM,
    x: 9,
    y: 10,
    facing: 'down',
    spriteClass: 'WHITE_MAGE',
    blockMovement: true,
  },
  {
    npcId: 'ELFHEIM_ELF_2',
    name: 'Elf',
    location: Location.ELFHEIM,
    x: 13,
    y: 8,
    facing: 'left',
    spriteClass: 'WHITE_MAGE',
    blockMovement: true,
  },
  {
    npcId: 'ELFHEIM_PRINCE',
    name: 'Elf Prince',
    location: Location.ELFHEIM,
    x: 10,
    y: 5,
    facing: 'down',
    spriteClass: 'KING',
    blockMovement: true,
  },

  // =========================================================================
  // MELMOND - Town of Decay
  // =========================================================================
  {
    npcId: 'MELMOND_OLD_MAN',
    name: 'Old Man',
    location: Location.MELMOND,
    x: 6,
    y: 11,
    facing: 'down',
    spriteClass: 'OLD_MAN',
    blockMovement: true,
  },
  {
    npcId: 'MELMOND_SAGE',
    name: 'Sage',
    location: Location.MELMOND,
    x: 12,
    y: 7,
    facing: 'down',
    spriteClass: 'RED_MAGE',
    blockMovement: true,
  },
  {
    npcId: 'MELMOND_WOMAN',
    name: 'Woman',
    location: Location.MELMOND,
    x: 8,
    y: 9,
    facing: 'left',
    spriteClass: 'WOMAN',
    blockMovement: true,
  },

  // =========================================================================
  // CRESCENT LAKE - Circle of Sages
  // =========================================================================
  {
    npcId: 'ELDER_SAGE',
    name: 'Elder Sage',
    location: Location.CRESCENT_LAKE,
    x: 8,
    y: 6,
    facing: 'down',
    spriteClass: 'RED_MAGE',
    blockMovement: true,
  },
  {
    npcId: 'SAGE_2',
    name: 'Second Sage',
    location: Location.CRESCENT_LAKE,
    x: 6,
    y: 8,
    facing: 'right',
    spriteClass: 'BLACK_MAGE',
    blockMovement: true,
  },
  {
    npcId: 'SAGE_3',
    name: 'Third Sage',
    location: Location.CRESCENT_LAKE,
    x: 10,
    y: 8,
    facing: 'left',
    spriteClass: 'WHITE_MAGE',
    blockMovement: true,
  },
  {
    npcId: 'SAGE_4',
    name: 'Fourth Sage',
    location: Location.CRESCENT_LAKE,
    x: 8,
    y: 10,
    facing: 'up',
    spriteClass: 'RED_MAGE',
    blockMovement: true,
  },

  // =========================================================================
  // GAIA - Mountain Town
  // =========================================================================
  {
    npcId: 'GAIA_MAN',
    name: 'Man',
    location: Location.GAIA,
    x: 9,
    y: 8,
    facing: 'down',
    spriteClass: 'FIGHTER',
    blockMovement: true,
  },
  {
    npcId: 'GAIA_WOMAN',
    name: 'Woman',
    location: Location.GAIA,
    x: 11,
    y: 10,
    facing: 'left',
    spriteClass: 'WOMAN',
    blockMovement: true,
  },

  // =========================================================================
  // ONRAC - Beach Resort
  // =========================================================================
  {
    npcId: 'ONRAC_WOMAN',
    name: 'Woman',
    location: Location.ONRAC,
    x: 7,
    y: 9,
    facing: 'down',
    spriteClass: 'WOMAN',
    blockMovement: true,
  },
  {
    npcId: 'ONRAC_FISHERMAN',
    name: 'Fisherman',
    location: Location.ONRAC,
    x: 13,
    y: 12,
    facing: 'down',
    spriteClass: 'THIEF',
    blockMovement: true,
  },

  // =========================================================================
  // SPECIAL NPCs - Caves and Dungeons
  // =========================================================================
  {
    npcId: 'MATOYA',
    name: 'Matoya',
    location: Location.MATOYA_CAVE,
    x: 7,
    y: 7,
    facing: 'down',
    spriteClass: 'WITCH',
    blockMovement: true,
  },
  {
    npcId: 'SARDA',
    name: 'Sarda',
    location: Location.SARDA_CAVE,
    x: 8,
    y: 5,
    facing: 'down',
    spriteClass: 'RED_MAGE',
    blockMovement: true,
  },
  {
    npcId: 'BAHAMUT',
    name: 'Bahamut',
    location: Location.BAHAMUT_CAVE,
    x: 8,
    y: 6,
    facing: 'down',
    spriteClass: 'DRAGON',
    blockMovement: true,
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all NPCs for a specific location
 */
export function getNPCsAtLocation(location: Location): NPCPlacement[] {
  return NPC_PLACEMENTS.filter(npc => npc.location === location);
}

/**
 * Get NPC by ID
 */
export function getNPCById(npcId: string): NPCPlacement | undefined {
  return NPC_PLACEMENTS.find(npc => npc.npcId === npcId);
}

/**
 * Get NPC at specific tile position
 */
export function getNPCAtPosition(
  location: Location,
  x: number,
  y: number
): NPCPlacement | undefined {
  return NPC_PLACEMENTS.find(
    npc => npc.location === location && npc.x === x && npc.y === y
  );
}

/**
 * Check if tile is blocked by an NPC
 */
export function isNPCBlocking(location: Location, x: number, y: number): boolean {
  const npc = getNPCAtPosition(location, x, y);
  return npc?.blockMovement ?? false;
}
